import { useState } from 'react';
import { Send, Loader2 } from 'lucide-react';
import { useAgents, useTestAgent } from '../hooks/useAgents';

export function TestPage() {
  const { data: agents } = useAgents();
  const test = useTestAgent();
  const [selected, setSelected] = useState('');
  const [message, setMessage] = useState('');
  const [history, setHistory] = useState<Array<{ agent: string; input: string; output: string; time: string }>>([]);

  async function handleSend() {
    const agent = selected || agents?.[0]?.name;
    if (!agent || !message.trim()) return;

    const input = message;
    setMessage('');

    const result = await test.mutateAsync({ name: agent, text: `@${agent} ${input}` });
    setHistory(prev => [{
      agent,
      input,
      output: result.response || (result.handled ? '(empty response)' : 'No agent matched'),
      time: new Date().toLocaleTimeString(),
    }, ...prev]);
  }

  return (
    <div className="space-y-4">
      {/* Input */}
      <div className="bg-surface border border-border rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-[auto_1fr] gap-3">
          <div>
            <label className="block text-[11px] text-zinc-500 mb-1">Agent</label>
            <select
              value={selected}
              onChange={e => setSelected(e.target.value)}
              className="input text-xs min-w-[140px]"
            >
              {agents?.map(a => (
                <option key={a.name} value={a.name}>{a.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] text-zinc-500 mb-1">Message</label>
            <div className="flex gap-2">
              <input
                value={message}
                onChange={e => setMessage(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSend()}
                placeholder="hello, what can you do?"
                className="input flex-1 text-xs"
              />
              <button
                onClick={handleSend}
                disabled={test.isPending || !message.trim()}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium bg-accent hover:bg-accent-2 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {test.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* History */}
      <div className="space-y-2">
        {history.length === 0 && (
          <div className="text-center py-12 text-zinc-600 text-sm">
            Send a message to test an agent
          </div>
        )}
        {history.map((h, i) => (
          <div key={i} className="bg-surface border border-border rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-2 text-[11px]">
              <span className="text-accent-2 font-medium">@{h.agent}</span>
              <span className="text-zinc-600">{h.time}</span>
            </div>
            <div className="text-xs text-zinc-400 bg-surface-2 rounded px-3 py-2">
              <span className="text-zinc-600">→ </span>{h.input}
            </div>
            <pre className="text-xs text-zinc-200 bg-zinc-900 rounded px-3 py-2 whitespace-pre-wrap overflow-x-auto max-h-[400px] overflow-y-auto">
              {h.output}
            </pre>
          </div>
        ))}
      </div>
    </div>
  );
}
